import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { deleteAgent, getAgents, getRuntimeProjects, getRuntimeTasks } from '../lib/api';
import { Bot, Plus, MoreVertical, Trash2, Edit, Eye, Code2, FlaskConical, ChevronDown, ChevronRight, Briefcase, MessageSquare, ArrowRight, Settings2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import AgentAvatar from '../components/AgentAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const getAgentDisplayName = (agent) => agent?.display_name_effective || agent?.display_name || agent?.name || 'Agent';
const getAgentBackendName = (agent) => agent?.name || agent?.display_name_effective || agent?.display_name || 'Agent';
const getExecutionAgentMetadata = (project) => {
  const metadata = project?.metadata || {};
  if (metadata?.agent_kind === 'agent_employee') return metadata;
  if (metadata?.product_type === 'execution_agent') return metadata;
  return null;
};

const AgentsPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [runtimeProjects, setRuntimeProjects] = useState([]);
  const [runtimeTasks, setRuntimeTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [showArchivedAgents, setShowArchivedAgents] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const activeAgents = agents.filter((agent) => agent.is_active);
  const archivedAgents = agents.filter((agent) => !agent.is_active);

  const fetchAgents = async () => {
    try {
      const [agentsRes, projectsRes, tasksRes] = await Promise.all([
        getAgents(),
        getRuntimeProjects(),
        getRuntimeTasks(),
      ]);
      setAgents(agentsRes.data || []);
      setRuntimeProjects(projectsRes.data || []);
      setRuntimeTasks(tasksRes.data || []);
    } catch (e) {
      console.error('Failed to fetch agents:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleDelete = async () => {
    if (!agentToDelete) return;
    try {
      await deleteAgent(agentToDelete.id);
      setAgents((current) => current.filter((a) => a.id !== agentToDelete.id));
    } catch (e) {
      console.error('Failed to delete agent:', e);
    } finally {
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      customer_service: 'Customer Service',
      appointment_setting: 'Appointment Setting',
      sales: 'Sales',
      routing: 'Routing Agent',
    };
    return labels[category] || category;
  };

  const getCategoryClass = (category) => {
    const classes = {
      customer_service: 'badge-customer-service',
      appointment_setting: 'badge-appointment-setting',
      sales: 'badge-sales',
      routing: 'badge-routing',
    };
    return classes[category] || '';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      customer_service: 'from-[#9333EA]/20 to-[#A855F7]/20',
      appointment_setting: 'from-[#22C55E]/20 to-[#4ADE80]/20',
      sales: 'from-[#EC4899]/20 to-[#F472B6]/20',
      routing: 'from-[#3B82F6]/20 to-[#60A5FA]/20',
    };
    return gradients[category] || 'from-[#9333EA]/20 to-[#EC4899]/20';
  };

  const getCategoryIconColor = (category) => {
    const colors = {
      customer_service: 'text-[#9333EA]',
      appointment_setting: 'text-[#22C55E]',
      sales: 'text-[#EC4899]',
      routing: 'text-[#3B82F6]',
    };
    return colors[category] || 'text-[#9333EA]';
  };

  const executionAgents = useMemo(() => (
    runtimeProjects
      .map((project) => {
        const meta = getExecutionAgentMetadata(project);
        if (!meta) return null;
        const relatedTasks = runtimeTasks.filter((task) => task.project_id === project.id);
        const runtimeProfile = meta.runtime_profile || {};
        return {
          project,
          runtimeProfile,
          relatedTasks,
          starterTask: relatedTasks[0] || null,
        };
      })
      .filter(Boolean)
  ), [runtimeProjects, runtimeTasks]);

  const totals = useMemo(() => ({
    conversational: activeAgents.length,
    employees: executionAgents.length,
    liveTasks: runtimeTasks.length,
  }), [activeAgents.length, executionAgents.length, runtimeTasks.length]);

  const openCreateAgentDialog = () => setCreateDialogOpen(true);
  const handleCreateConversationalAgent = () => {
    setCreateDialogOpen(false);
    navigate('/agents/new');
  };
  const handleCreateAgentEmployee = () => {
    setCreateDialogOpen(false);
    navigate('/agent-employees/new');
  };

  return (
    <Layout>
      <div className="p-8" data-testid="agents-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Agent Dashboard</h1>
            <p className="text-muted-foreground mt-1">A unified view of conversational agents and agent employees. Open an agent to chat, monitor, and manage it. Use Configure for setup only.</p>
          </div>
          <Button className="btn-gradient text-white rounded-lg gap-2" data-testid="new-agent-button" onClick={openCreateAgentDialog}>
            <Plus className="w-4 h-4" />
            Add new agent
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="apollo-card p-6">
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Conversational agents</div>
            <div className="text-3xl font-semibold mt-2 font-['Outfit']">{totals.conversational}</div>
          </div>
          <div className="apollo-card p-6">
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Agent employees</div>
            <div className="text-3xl font-semibold mt-2 font-['Outfit']">{totals.employees}</div>
          </div>
          <div className="apollo-card p-6">
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Open employee tasks</div>
            <div className="text-3xl font-semibold mt-2 font-['Outfit']">{totals.liveTasks}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading agents...</div>
        ) : (
          <div className="space-y-10">
            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold font-['Outfit']">Conversational agents</h2>
                  <p className="text-sm text-muted-foreground">Customer-facing agents you can open and chat with directly.</p>
                </div>
              </div>

              {activeAgents.length === 0 && archivedAgents.length === 0 ? (
                <div className="text-center py-16 apollo-card">
                  <Bot className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2 font-['Outfit']">No conversational agents yet</h2>
                  <p className="text-muted-foreground mb-6">Create your first conversational agent to start chatting and configuring its behavior.</p>
                  <Button className="btn-gradient text-white rounded-lg gap-2" onClick={openCreateAgentDialog}>
                    <Plus className="w-4 h-4" /> Add new agent
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeAgents.map((agent, i) => (
                    <div key={agent.id} className="apollo-card animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`agent-card-${agent.id}`}>
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <AgentAvatar
                              name={getAgentBackendName(agent)}
                              avatarUrl={agent.avatar_url}
                              className={`h-10 w-10 rounded-lg border border-border ${agent.avatar_url ? '' : `bg-gradient-to-br ${getCategoryGradient(agent.category)}`}`}
                              fallbackClassName={`font-semibold ${getCategoryIconColor(agent.category)} ${agent.avatar_url ? '' : 'bg-transparent'}`}
                            />
                            <div>
                              <h3 className="font-medium">{getAgentBackendName(agent)}</h3>
                              {getAgentDisplayName(agent) !== getAgentBackendName(agent) && <p className="text-xs text-muted-foreground">Widget name: {getAgentDisplayName(agent)}</p>}
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryClass(agent.category)}`}>{getCategoryLabel(agent.category)}</span>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 hover:bg-secondary rounded-lg"><MoreVertical className="w-4 h-4 text-muted-foreground" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/agents/${agent.id}`} className="flex items-center gap-2"><Eye className="w-4 h-4" /> Open agent</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/agents/${agent.id}/edit`} className="flex items-center gap-2"><Settings2 className="w-4 h-4" /> Configure</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/agents/${agent.id}/proving-grounds`} className="flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Advanced testing</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/agents/${agent.id}/widget`} className="flex items-center gap-2"><Code2 className="w-4 h-4" /> Widget & Embed</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-500" onClick={() => { setAgentToDelete(agent); setDeleteDialogOpen(true); }}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{agent.description || 'No description'}</p>

                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`status-dot ${agent.is_active ? 'active' : 'inactive'}`}></span>
                            <span className="text-muted-foreground">{agent.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                          <span className="text-muted-foreground text-xs">{agent.llm_provider} / {agent.llm_model?.split('-').slice(0, 2).join('-')}</span>
                        </div>

                        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-2">
                          <Link to={`/agents/${agent.id}`} className="text-sm text-primary hover:text-[#EC4899] transition-colors inline-flex items-center gap-1">
                            Open agent <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                          <Button variant="outline" size="sm" className="rounded-lg gap-2" asChild>
                            <Link to={`/agents/${agent.id}/edit`}><Edit className="w-4 h-4" /> Configure</Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {archivedAgents.length > 0 && (
                <div className="mt-8">
                  <button type="button" onClick={() => setShowArchivedAgents((prev) => !prev)} className="mb-4 flex w-full items-center justify-between rounded-xl border border-border bg-card/50 px-4 py-3 text-left hover:bg-card">
                    <div>
                      <h2 className="text-lg font-semibold font-['Outfit']">Archived legacy agents</h2>
                      <p className="text-sm text-muted-foreground">{showArchivedAgents ? 'Hide older inactive agents when you do not need them.' : `Hidden by default. ${archivedAgents.length} archived ${archivedAgents.length === 1 ? 'agent' : 'agents'} available if you need rollback or cleanup.`}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{showArchivedAgents ? 'Hide' : 'Show'}</span>
                      {showArchivedAgents ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                  </button>

                  {showArchivedAgents && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-80">
                      {archivedAgents.map((agent, i) => (
                        <div key={agent.id} className="apollo-card animate-fade-in border-dashed" style={{ animationDelay: `${i * 0.05}s` }}>
                          <div className="p-5">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <AgentAvatar name={getAgentBackendName(agent)} avatarUrl={agent.avatar_url} className={`h-10 w-10 rounded-lg border border-border ${agent.avatar_url ? '' : `bg-gradient-to-br ${getCategoryGradient(agent.category)}`}`} fallbackClassName={`font-semibold ${getCategoryIconColor(agent.category)} ${agent.avatar_url ? '' : 'bg-transparent'}`} />
                                <div>
                                  <h3 className="font-medium">{getAgentBackendName(agent)}</h3>
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryClass(agent.category)}`}>{getCategoryLabel(agent.category)}</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{agent.description || 'No description'}</p>
                            <div className="flex items-center justify-between gap-3 text-sm">
                              <div className="flex items-center gap-2"><span className="status-dot inactive"></span><span className="text-muted-foreground">Archived</span></div>
                              <span className="text-muted-foreground text-xs">{agent.llm_provider} / {agent.llm_model?.split('-').slice(0, 2).join('-')}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold font-['Outfit']">Agent employees</h2>
                  <p className="text-sm text-muted-foreground">Operational agent employees with their own views and separate configuration.</p>
                </div>
                <Button variant="outline" className="rounded-lg" onClick={() => navigate('/agent-employees/new')}>
                  <Plus className="w-4 h-4 mr-2" /> Add agent employee
                </Button>
              </div>

              {executionAgents.length === 0 ? (
                <div className="apollo-card p-10 text-center">
                  <Briefcase className="w-14 h-14 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2 font-['Outfit']">No agent employees yet</h3>
                  <p className="text-muted-foreground mb-5">Create an agent employee when you want Apollo to operate on work behind the scenes.</p>
                  <Button className="btn-gradient text-white rounded-lg gap-2" onClick={() => navigate('/agent-employees/new')}>
                    <Plus className="w-4 h-4" /> Create agent employee
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {executionAgents.map(({ project, runtimeProfile, relatedTasks, starterTask }) => (
                    <div key={project.id} className="apollo-card p-5 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">Agent employee</Badge>
                            <Badge variant="outline">{project.status || 'active'}</Badge>
                          </div>
                          <h3 className="text-xl font-semibold">{runtimeProfile.name || project.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{runtimeProfile.role || 'Agent employee'}</p>
                        </div>
                        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Briefcase className="w-5 h-5" /></div>
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-3">{runtimeProfile.mission || project.description || 'No mission recorded yet.'}</p>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-xl border border-border/70 p-3">
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Tasks</div>
                          <div className="text-xl font-semibold mt-2">{relatedTasks.length}</div>
                        </div>
                        <div className="rounded-xl border border-border/70 p-3">
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Starter task</div>
                          <div className="text-sm font-semibold mt-2 line-clamp-2">{starterTask?.title || 'Not launched yet'}</div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-border flex items-center justify-between gap-2">
                        <Link to={`/agent-employees/${project.id}`} className="text-sm text-primary hover:text-[#EC4899] transition-colors inline-flex items-center gap-1">
                          Open employee view <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/agent-employees/${project.id}/edit`}><Edit className="w-4 h-4 mr-2" /> Configure</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add new agent</DialogTitle>
              <DialogDescription>
                Choose whether you want a conversational agent for chats and channels, or an agent employee for behind-the-scenes execution work.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              <button type="button" onClick={handleCreateConversationalAgent} className="rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-primary/5" data-testid="create-conversational-agent-option">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><MessageSquare className="h-6 w-6" /></div>
                <h3 className="text-lg font-semibold">Conversational agent</h3>
                <p className="mt-2 text-sm text-muted-foreground">Build a website or channel-facing agent for chat, support, lead capture, scheduling, and customer conversations.</p>
              </button>
              <button type="button" onClick={handleCreateAgentEmployee} className="rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-primary/5" data-testid="create-agent-employee-option">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Briefcase className="h-6 w-6" /></div>
                <h3 className="text-lg font-semibold">Agent employee</h3>
                <p className="mt-2 text-sm text-muted-foreground">Configure an execution agent that works inside Apollo behind the scenes.</p>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Agent</AlertDialogTitle>
              <AlertDialogDescription>Are you sure you want to delete "{agentToDelete?.name}"? This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-lg">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AgentsPage;
